import React from 'react';
import { Trophy, Award, Brain, ChartBar, Leaf, Beaker, Zap, FlaskConical, Microscope } from 'lucide-react';

const PrizeSection = () => {
    const categories = [
        {
            name: "Social Impact",
            icon: <Brain />,
            description: "Innovations driving societal change",
            gradientClass: "from-rose-400 via-fuchsia-500 to-indigo-500"
        },
        {
            name: "Economic Impact",
            icon: <ChartBar />,
            description: "Solutions revolutionizing Economic landscapes",
            gradientClass: "from-blue-400 via-cyan-500 to-teal-500"
        },
        {
            name: "Visual Physics Software",
            icon: <Leaf />,
            description: "Software Representation of Physics",
            gradientClass: "from-green-400 via-emerald-500 to-teal-500"
        }
    ];

    return (
        <section className="relative pb-24 pt-12 bg-gradient-to-b from-white to-blue-50 overflow-hidden">
            <div className="absolute inset-0">
                {[...Array(20)].map((_, i) => (
                    <div
                        key={i}
                        className="absolute w-2 h-2 rounded-full bg-blue-500 opacity-20 animate-pulse"
                        style={{
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                            animationDelay: `${Math.random() * 2}s`,
                        }}
                    />
                ))}
            </div>

            {/* Prize Pool Showcase */}
            <div className="max-w-7xl mx-auto px-4 relative z-10">
                <div className="relative mb-24">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-3xl blur-3xl opacity-20 transform -rotate-1" />
                    <div className="relative bg-white rounded-3xl shadow-2xl p-12 overflow-hidden">
                        <div className="text-center relative z-10 flex flex-col items-center justify-center">
                            <div className="inline-flex items-center justify-center space-x-2 mb-6">
                                <div className="relative">
                                    <div className="absolute inset-0 animate-ping">
                                        <Trophy className="w-10 h-10 text-yellow-500 opacity-30" />
                                    </div>
                                    <Trophy className="w-10 h-10 text-yellow-500 relative" />
                                </div>
                            </div>
                            <h2 className="text-6xl md:text-7xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
                                ₹60,000
                            </h2>
                            <div className="text-2xl text-gray-600 font-medium mb-8">Total Prize Pool</div>

                            {/* Prize Structure Overview */}
                            <div className="max-w-2xl mx-auto bg-blue-50 rounded-xl p-6">
                                <p className="text-blue-800 text-lg font-medium">
                                    Three winners will be selected from each category:
                                </p>
                                <div className="grid grid-cols-3 gap-4 mt-4">
                                    <div className="flex flex-col items-center">
                                        <Award className="w-8 h-8 text-yellow-500 mb-2" />
                                        <span className="text-gray-700 font-semibold">1st Prize</span>
                                    </div>
                                    <div className="flex flex-col items-center">
                                        <Award className="w-8 h-8 text-gray-400 mb-2" />
                                        <span className="text-gray-700 font-semibold">2nd Prize</span>
                                    </div>
                                    <div className="flex flex-col items-center">
                                        <Award className="w-8 h-8 text-orange-500 mb-2" />
                                        <span className="text-gray-700 font-semibold">3rd Prize</span>
                                    </div>
                                </div>
                                <p className="text-gray-600 text-1xl md:text-1xl font-bold mt-6">
                                    All participants will receive certificates. Winners will be selected based on innovation, impact, and technical excellence.
                                </p>
                            </div>
                            <div className="mt-8 flex space-x-4">
                                <Beaker className="w-8 h-8 text-blue-400 animate-bounce" />
                                <Zap className="w-8 h-8 text-yellow-400 animate-pulse" />
                                <FlaskConical className="w-8 h-8 text-green-400 animate-bounce" />
                                <Microscope className="w-8 h-8 text-purple-400 animate-pulse" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Category Cards */}
                <div className="grid md:grid-cols-3 gap-8 mb-16">
                    {categories.map((category, idx) => (
                        <div key={idx} className="group">
                            <div className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-shadow duration-300">
                                {/* Category Header */}
                                <div className={`w-16 h-16 rounded-full bg-gradient-to-r ${category.gradientClass} p-4 mb-6 text-white`}>
                                    {category.icon}
                                </div>
                                <h3 className="text-2xl font-bold text-gray-800 mb-4">{category.name}</h3>
                                <p className="text-gray-600 mb-6">{category.description}</p>

                                {/* Prize Positions */}
                                <div className="space-y-4">
                                    <div className="flex items-center space-x-3 bg-yellow-50 p-3 rounded-lg">
                                        <Award className="w-6 h-6 text-yellow-500" />
                                        <span className="text-gray-700 font-medium">First Position</span><span className='text-gray-800 flex-auto text-left font-semibold' >₹10,000</span>
                                    </div>
                                    <div className="flex items-center space-x-3 bg-gray-50 p-3 rounded-lg">
                                        <Award className="w-6 h-6 text-gray-400" />
                                        <span className="text-gray-700 font-medium">Second Position</span><span className='text-gray-800 flex-auto text-left font-semibold' >₹6,000</span>
                                    </div>
                                    <div className="flex items-center space-x-3 bg-orange-50 p-3 rounded-lg">
                                        <Award className="w-6 h-6 text-orange-500" />
                                        <span className="text-gray-700 font-medium">Third Position</span><span className='text-gray-800 flex-auto text-left font-semibold' >₹4,000</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Bottom Note */}
                {/* <div className="text-center">
                    <p className="text-gray-600">
                        All participants will receive certificates. Winners will be selected based on innovation, impact, and technical excellence.
                    </p>
                </div> */}
            </div>
        </section>
    );
};

export default PrizeSection;